import { makeAutoObservable } from 'mobx';
import {
    getRRule,
    convertUTCWeekDayToLocal,
    convertUTCHourToLocalHour,
} from '../../containers/reports/reports-helpers';
import { SCHEDULE_DAYS, SCHEDULE_DAY_POS } from '../../utils/constants';
import { RRule } from 'rrule';

class Report {
    selectedUserListsIds = [];
    lists = [];
    allUserLists = false;
    date = null;
    name = 'Untitled report';
    id = null;
    recipients = null;
    customEmailSubject = null;
    tags = [];

    reportDays = null;

    reportLocale = 'auto';

    reportType = null;

    dryRun = false;

    rruleString = null;
    rruleObject = null;

    frequency = null;
    localWeekDay = null;
    localHourOfDay = 7;
    localHourOfDayString = '07:00 GMT';
    weekdayPosition = 1;

    utcWeekDay = null;
    utcHourOfDay = 7;

    frequencyText = '-';

    constructor(reportData, companyId) {
        makeAutoObservable(this);
        this.companyId = companyId;

        const sanitizeTags = (value) =>
            Array.isArray(value)
                ? Array.from(
                      new Set(
                          value
                              .filter((tag) => typeof tag === 'string')
                              .map((tag) => tag.trim())
                              .filter((tag) => tag.length > 0)
                      )
                  )
                : [];

        if (reportData) {
            this._rawData = reportData.id ? reportData : null;
            this.name = reportData.name;
            this.id = reportData.id;

            //----------------------------------------------
            //---- Coming from the form (New)
            if (reportData.allEmployees) {
                this.allUserLists = true;
            } else {
                this.selectedUserListsIds = reportData.employeeLists;
            }
            if (reportData.scheduleFrequency) {
                this.rruleObject = getRRule({
                    frequency: reportData.scheduleFrequency,
                    weekDay: reportData.scheduleWeekday,
                    hourOfDay: reportData.scheduleHour,
                    weekdayPosition: reportData.scheduleWeekdayPosition,
                });
                this.rruleString = this.rruleObject.toString();
            }
            if (reportData.scheduleFrequency) {
                this.frequency = reportData.scheduleFrequency;
                this.localWeekDay = reportData.scheduleWeekday;
                this.localHourOfDay = reportData.scheduleHour;
                this.weekdayPosition = reportData.scheduleWeekdayPosition;
            }
            if (reportData.reportType) {
                this.reportType = reportData.reportType;
            }
            this.recipients =
                reportData.recipientsEmails &&
                reportData.recipientsEmails.replace(/\s/g, '').split(/[;,]/);
            this.customEmailSubject = reportData.customEmailSubject;
            if (reportData.reportDays != null && reportData.reportDays !== '') {
                this.reportDays = reportData.reportDays;
            }
            if (reportData.reportLocale) {
                this.reportLocale = reportData.reportLocale;
            }
            this.tags = sanitizeTags(
                reportData.reportTags || reportData.tags || []
            );
            this.dryRun = !!reportData.dryRun;

            //----------------------------------------------
            //------ Coming from the server (Edit)
            if (reportData.definition) {
                this.reportType = reportData.definition.reportType;
                if (reportData.definition.listIds === 'all') {
                    this.allUserLists = true;
                } else {
                    this.selectedUserListsIds = reportData.definition.listIds;
                }
                this.customEmailSubject = reportData.definition.emailSubject;
                this.recipients =
                    reportData.definition.recipients &&
                    reportData.definition.recipients.join(', ');
                this.tags = sanitizeTags(reportData.definition.tags);
                if (reportData.definition.reportDays != null) {
                    this.reportDays = reportData.definition.reportDays;
                }
                this.reportLocale =
                    reportData.definition.locale &&
                    String(reportData.definition.locale).trim()
                        ? String(reportData.definition.locale).trim()
                        : 'auto';
                this.dryRun = !!reportData.definition.dryRun;
            }
            if (reportData.rrule) {
                this.rruleObject = RRule.fromString(reportData.rrule);
                this.resolveRRuleObject(this.rruleObject);
                this.rruleString = reportData.rrule;
            }
            this.lists = reportData.lists;

            if (this.rruleObject) {
                this.setFrequencyText(this.rruleObject);
            }
        }
    }

    resolveRRuleObject = (rruleObject) => {
        let freq = RRule.FREQUENCIES[rruleObject.origOptions.freq];
        if (freq === 'WEEKLY' && rruleObject.origOptions.interval === 2) {
            freq = 'BIWEEKLY';
        }
        this.frequency = freq;
        //This is stored in UTC
        this.utcWeekDay =
            SCHEDULE_DAYS[rruleObject.origOptions.byweekday[0].weekday].id;
        this.utcHourOfDay = rruleObject.origOptions.byhour;

        //Convert to local time to display
        this.localHourOfDayString = convertUTCHourToLocalHour(
            this.utcHourOfDay
        );
        this.localHourOfDay = extractHourFromTimeString(
            this.localHourOfDayString
        );

        this.localWeekDay = convertUTCWeekDayToLocal(
            this.utcWeekDay,
            this.utcHourOfDay,
            this.localHourOfDay
        );

        this.weekdayPosition = rruleObject.origOptions.bysetpos || 1;

        function extractHourFromTimeString(timeString) {
            const [hourString] = timeString.split(':');
            const hour = parseInt(hourString, 10);
            return hour;
        }
    };

    setName = (value) => {
        this.name = value;
    };

    setFrequencyText = (rrule) => {
        if (!rrule) return (this.frequencyText = '-');

        //--- Clone the rrule object to avoid changing the original
        const rruleObjectClone = rrule.clone();
        rruleObjectClone.origOptions.byweekday = RRule[this.localWeekDay];

        //--- Get a general text for the frequency
        const rruleAsText = rruleObjectClone.toText();
        this.frequencyText =
            rruleAsText.charAt(0).toUpperCase() + rruleAsText.slice(1);

        //--- If it's a monthly report, add the weekday position
        if (this.frequency === 'MONTHLY') {
            const weekdayPositionObject = SCHEDULE_DAY_POS.find(
                (pos) => pos.id === this.weekdayPosition
            );
            const weekdayPositionText = weekdayPositionObject
                ? weekdayPositionObject.name.toLowerCase()
                : this.weekdayPosition;

            this.frequencyText = this.frequencyText.replace(
                /(on\s)(\w+)/,
                `$1${' the ' + weekdayPositionText} $2`
            );
        }

        this.frequencyText =
            this.frequencyText + ' at ' + this.localHourOfDayString;
    };

    get link() {
        return `/reports/${this.id}`;
    }

    toDTO(extraData) {
        const isDepartmentManager = this.reportType === 'department-manager';
        const definition = {
            listIds:
                this.allUserLists || isDepartmentManager
                    ? 'all'
                    : this.selectedUserListsIds,
            emailSubject: this.customEmailSubject,
            recipients: this.recipients,
            reportType: this.reportType,
            tags: this.tags,
            dryRun: !!this.dryRun,
        };
        if (
            isDepartmentManager &&
            this.reportDays != null &&
            this.reportDays !== '' &&
            !Number.isNaN(Number(this.reportDays))
        ) {
            definition.reportDays = Number(this.reportDays);
        }
        if (isDepartmentManager) {
            const loc =
                this.reportLocale && String(this.reportLocale).trim()
                    ? String(this.reportLocale).trim()
                    : 'auto';
            definition.locale = loc;
        }
        return {
            id: this.id && this.id !== '' ? this.id : null,
            name: this.name.replace('&nbsp;', ' '),
            definition,
            rrule: this.rruleString,
            ...extraData,
        };
    }
}

export default Report;
